import React, { useState, useContext, useEffect } from "react"
import i18n from "../../services/i18n"
import {
  Modal,
  Tabs,
  Avatar,
  Typography,
  Space,
  Button,
  Upload,
  Input,
  Form,
  message,
  Tag,
  Row,
  Col,
  Card,
  Switch,
  Divider,
  Progress,
  Badge,
  Alert,
} from "antd"
import {
  User,
  Camera,
  Trash2,
  Lock,
  Key,
  Shield,
  Bell,
  Moon,
  Sun,
  Activity,
  CheckCircle,
  Mail,
  Phone,
  Calendar,
  Save,
  Globe,
  Sliders,
  Send,
  Check,
  Info,
} from "react-feather"
import { AuthContext } from "../../contexts/auth/AuthContext"
import { ThemeContext, ThemeActions } from "../../contexts/ThemeContex"
import { useTranslation } from "react-i18next"
import { CHANGE_USER_PASSWORD, UPLOAD_PROFILE_PHOTO, DELETE_PROFILE_PHOTO } from "../../rest-api/mutations/user"
import { GET_PROFILE_PHOTO } from "../../rest-api/queries/user"
import { useMutation } from "@tanstack/react-query"

export default function UserProfileModal({
  visible,
  onClose,
}: {
  visible: boolean
  onClose: () => void
}) {
  const { authState } = useContext(AuthContext)
  const { themeState, themeDispatch } = useContext(ThemeContext)
  const { t } = useTranslation()
  const isDark = themeState.currentTheme === "dark"

  const user = authState.user
  const userId = user?.id || (user as any)?.userId || 1
  const username = user?.userName || user?.email || "Administrator"
  const managerData = user?.manager

  // Dynamic Role display
  const userRole =
    user?.userName === "Administrator" ||
    (user as any)?.role === 1 ||
    !user?.managerId
      ? String(i18n.t("superAdminRole"))
      : String(i18n.t("managerWord"))

  const defaultFullName =
    (managerData as any)?.fullName ||
    managerData?.name ||
    (username === "Administrator" ? "Super-admin" : username)
  const defaultPhone = (managerData as any)?.phone || "+998 90 123 45 67"
  const defaultEmail = user?.email || "admin@zenith-erp.uz"

  const [activeTab, setActiveTab] = useState<string>("info")
  const [photo, setPhoto] = useState<string>(
    localStorage.getItem(`user_profile_photo_${userId}`) ||
      localStorage.getItem("user_profile_photo") ||
      (user as any)?.photoUrl ||
      ""
  )
  const [uploadingPhoto, setUploadingPhoto] = useState<boolean>(false)
  const [soundEnabled, setSoundEnabled] = useState<boolean>(() => {
    return localStorage.getItem("crm_sound_enabled") !== "false"
  })

  // Personal Info Form State (scoped per user)
  const [fullName, setFullName] = useState<string>(() => {
    return (
      localStorage.getItem(`crm_user_fullname_${userId}`) ||
      localStorage.getItem("crm_user_fullname") ||
      defaultFullName
    )
  })
  const [phone, setPhone] = useState<string>(() => {
    return (
      localStorage.getItem(`crm_user_phone_${userId}`) ||
      localStorage.getItem("crm_user_phone") ||
      defaultPhone
    )
  })

  // Email & Verification Code State
  const [savedEmail, setSavedEmail] = useState<string>(() => {
    return (
      localStorage.getItem(`crm_user_email_${userId}`) ||
      localStorage.getItem("crm_user_email") ||
      defaultEmail
    )
  })
  const [emailInput, setEmailInput] = useState<string>(() => {
    return (
      localStorage.getItem(`crm_user_email_${userId}`) ||
      localStorage.getItem("crm_user_email") ||
      defaultEmail
    )
  })
  const [isEmailVerified, setIsEmailVerified] = useState<boolean>(() => {
    return localStorage.getItem(`crm_user_email_verified_${userId}`) !== "false"
  })
  const [sentCode, setSentCode] = useState<string>("")
  const [inputCode, setInputCode] = useState<string>("")
  const [codeCountdown, setCodeCountdown] = useState<number>(0)
  const [showCodeInput, setShowCodeInput] = useState<boolean>(false)

  // Mutations
  const [pwdForm] = Form.useForm()
  const [pwdStrength, setPwdStrength] = useState<number>(0)
  const { mutate: changePassword, isLoading: changingPwd } = useMutation(CHANGE_USER_PASSWORD)
  const { mutate: uploadPhotoMutation } = useMutation(UPLOAD_PROFILE_PHOTO)
  const { mutate: deletePhotoMutation } = useMutation(DELETE_PROFILE_PHOTO)

  useEffect(() => {
    const updatePhoto = () => {
      setPhoto(localStorage.getItem("user_profile_photo") || "")
    }
    window.addEventListener("profile_photo_updated", updatePhoto)
    if (visible) {
      GET_PROFILE_PHOTO().then((res: any) => {
        const url = res?.PhotoUrl || res?.photoUrl
        const fileName = res?.FileName || res?.fileName
        if (url) {
          setPhoto(url)
          localStorage.setItem("user_profile_photo", url)
          if (fileName) localStorage.setItem("user_profile_photo_filename", fileName)
        }
      }).catch(() => {})
    }
    return () => window.removeEventListener("profile_photo_updated", updatePhoto)
  }, [visible])

  // Timer for verification code
  useEffect(() => {
    if (codeCountdown > 0) {
      const timer = setTimeout(() => setCodeCountdown(codeCountdown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [codeCountdown])

  const handleBeforeUpload = (file: File) => {
    const isImage = file.type.startsWith("image/")
    if (!isImage) {
      message.error(t("msgOnlyImageFiles"))
      return false
    }
    const isLt3M = file.size / 1024 / 1024 < 3
    if (!isLt3M) {
      message.error(t("msgImageTooLarge"))
      return false
    }

    const reader = new FileReader()
    reader.onload = (e) => {
      const base64 = e.target?.result as string
      if (base64) {
        setUploadingPhoto(true)
        uploadPhotoMutation(
          {
            PhotoBase64: base64,
            OldFileName: localStorage.getItem("user_profile_photo_filename") || undefined,
          },
          {
            onSuccess: (res: any) => {
              setUploadingPhoto(false)
              const minioUrl = res?.Url || res?.url || base64
              const fileName = res?.FileName || res?.fileName || ""
              setPhoto(minioUrl)
              localStorage.setItem("user_profile_photo", minioUrl)
              if (fileName) {
                localStorage.setItem("user_profile_photo_filename", fileName)
              }
              window.dispatchEvent(new Event("profile_photo_updated"))
              message.success(t("msgAvatarSavedMinio"))
            },
            onError: () => {
              setUploadingPhoto(false)
              setPhoto(base64)
              localStorage.setItem("user_profile_photo", base64)
              window.dispatchEvent(new Event("profile_photo_updated"))
              message.info("Profil rasmi yangilandi")
            },
          }
        )
      }
    }
    reader.readAsDataURL(file)
    return false
  }

  const handleRemovePhoto = () => {
    const oldFileName = localStorage.getItem("user_profile_photo_filename")
    if (oldFileName) {
      deletePhotoMutation({ OldFileName: oldFileName })
    }
    setPhoto("")
    localStorage.removeItem("user_profile_photo")
    localStorage.removeItem("user_profile_photo_filename")
    window.dispatchEvent(new Event("profile_photo_updated"))
    message.info("Profil rasmi olib tashlandi")
  }

  // Send Email Verification Code
  const handleSendVerificationCode = () => {
    if (!emailInput || !emailInput.includes("@")) {
      message.error(t("msgEnterValidEmail"))
      return
    }

    const randomCode = Math.floor(100000 + Math.random() * 900000).toString()
    setSentCode(randomCode)
    setShowCodeInput(true)
    setCodeCountdown(60)

    // Notification with simulated code
    message.info(
      `Tasdiqlash kodi ${emailInput} manziliga yuborildi: [ ${randomCode} ]`,
      8
    )
  }

  // Verify Email Code
  const handleVerifyEmail = () => {
    if (!inputCode) {
      message.error(t("msgEnter6DigitCode"))
      return
    }
    if (inputCode.trim() !== sentCode) {
      message.error(t("msgWrongCode"))
      return
    }

    setSavedEmail(emailInput)
    setIsEmailVerified(true)
    setShowCodeInput(false)
    setInputCode("")
    localStorage.setItem("crm_user_email", emailInput)
    localStorage.setItem("crm_user_email_verified", "true")
    message.success(t("msgEmailVerified"))
  }

  const handleSavePersonalInfo = () => {
    localStorage.setItem(`crm_user_fullname_${userId}`, fullName)
    localStorage.setItem(`crm_user_phone_${userId}`, phone)
    localStorage.setItem("crm_user_fullname", fullName)
    localStorage.setItem("crm_user_phone", phone)
    if (emailInput === savedEmail) {
      localStorage.setItem(`crm_user_email_${userId}`, savedEmail)
      localStorage.setItem("crm_user_email", savedEmail)
    }
    message.success(t("msgProfileSaved"))
  }

  const handlePasswordStrength = (val: string) => {
    let score = 0
    if (val.length >= 6) score += 30
    if (val.length >= 8) score += 20
    if (/[A-Z]/.test(val)) score += 25
    if (/[0-9]/.test(val)) score += 25
    setPwdStrength(Math.min(100, score))
  }

  const handlePasswordSubmit = (values: any) => {
    if (!values.currentPassword) {
      message.error(t("msgEnterCurrentPassword"))
      return
    }

    if (values.newPassword !== values.confirmPassword) {
      message.error(t("msgPasswordsMismatch"))
      return
    }

    changePassword(
      {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
        confirmPassword: values.confirmPassword,
      },
      {
        onSuccess: () => {
          message.success(t("msgPasswordChanged"))
          pwdForm.resetFields()
          setPwdStrength(0)
        },
        onError: (err: any) => {
          message.error(err?.response?.data?.message || err?.response?.data?.Message || "Parolni o'zgartirishda xatolik yuz berdi")
        },
      }
    )
  }

  // Dynamic Theme Colors
  const cardBg = isDark ? "#211B2B" : "#F8FAFC"
  const cardBorder = isDark ? "rgba(255, 255, 255, 0.08)" : "#E2E8F0"
  const titleColor = isDark ? "#FFFFFF" : "#0F172A"
  const labelColor = isDark ? "#CBD5E1" : "#334155"
  const inputBg = isDark ? "#1A1523" : "#FFFFFF"
  const inputBorder = isDark ? "rgba(255, 255, 255, 0.15)" : "#CBD5E1"
  const textColor = isDark ? "#F8FAFC" : "#0F172A"

  const inputStyle: React.CSSProperties = {
    backgroundColor: inputBg,
    borderColor: inputBorder,
    color: textColor,
    borderRadius: 6,
    height: 38,
  }

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={740}
      bodyStyle={{ padding: 0, overflow: "hidden", borderRadius: 12 }}
      centered
      className={isDark ? "dark-profile-modal" : "light-profile-modal"}
    >
      {/* 1. Header Banner with Profile Hero */}
      <div
        style={{
          background: "linear-gradient(135deg, #1E3A8A 0%, #2563EB 50%, #3B82F6 100%)",
          padding: "26px 24px 20px 24px",
          position: "relative",
          color: "#FFFFFF",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
          {/* Avatar with Camera Trigger */}
          <div style={{ position: "relative" }}>
            <Avatar
              size={84}
              src={photo || undefined}
              icon={!photo && <User size={42} color="#FFFFFF" />}
              style={{
                backgroundColor: "rgba(255,255,255,0.2)",
                border: "3px solid #FFFFFF",
                boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {username.charAt(0).toUpperCase()}
            </Avatar>

            <Upload beforeUpload={handleBeforeUpload} showUploadList={false} accept="image/*">
              <Button
                type="primary"
                shape="circle"
                size="small"
                icon={<Camera size={13} />}
                style={{
                  position: "absolute",
                  bottom: 0,
                  right: 0,
                  backgroundColor: "#2563EB",
                  borderColor: "#FFFFFF",
                  borderWidth: 2,
                  boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              />
            </Upload>
          </div>

          {/* User Info Details */}
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span style={{ color: "#FFFFFF", margin: 0, fontWeight: 700, fontSize: 18 }}>
                {fullName || username}
              </span>
              <Tag
                color="gold"
                style={{
                  fontWeight: 600,
                  borderRadius: 6,
                  padding: "1px 8px",
                  fontSize: 12,
                  border: "none",
                }}
              >
                {userRole}
              </Tag>
              <Badge status="success" text={<span style={{ color: "#BBF7D0", fontSize: 12 }}>Online</span>} />
            </div>

            <div style={{ marginTop: 6, fontSize: 13, opacity: 0.95, display: "flex", gap: 16, flexWrap: "wrap" }}>
              <span>👤 Login: <strong>{username}</strong></span>
              <span>📧 {savedEmail}</span>
            </div>
          </div>
        </div>
      </div>

      {/* 2. Tabs Navigation */}
      <div style={{ padding: "16px 24px 24px 24px", backgroundColor: isDark ? "#181320" : "#FFFFFF" }}>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          {/* TAB 1: Shaxsiy Ma'lumotlar & Rasm & Email Tasdiqlash */}
          <Tabs.TabPane
            key="info"
            tab={
              <Space>
                <User size={15} />
                <span>{t("upBasicInfo")}</span>
              </Space>
            }
          >
            <div style={{ marginTop: 8 }}>
              {/* Card 1: Avatar */}
              <Card
                size="small"
                title={<span style={{ color: titleColor, fontWeight: 600, fontSize: 14 }}>Profil Rasmi Boshqaruvi</span>}
                style={{
                  marginBottom: 16,
                  borderRadius: 8,
                  backgroundColor: cardBg,
                  borderColor: cardBorder,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
                  <Space align="center" size={16}>
                    <Avatar
                      size={54}
                      src={photo || undefined}
                      icon={!photo && <User size={26} color="#64748B" />}
                      style={{ backgroundColor: isDark ? "#1A1523" : "#F1F5F9", border: `1px solid ${inputBorder}` }}
                    />
                    <div>
                      <Typography.Text style={{ color: titleColor, fontWeight: 600, display: "block" }}>
                        Foydalanuvchi avatari
                      </Typography.Text>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        JPG, PNG yoki WEBP (maksimal 3MB)
                      </Typography.Text>
                    </div>
                  </Space>

                  <Space>
                    <Upload beforeUpload={handleBeforeUpload} showUploadList={false} accept="image/*">
                      <Button
                        type="primary"
                        icon={<Camera size={14} style={{ marginRight: 6 }} />}
                        style={{ borderRadius: 6, backgroundColor: "#2563EB" }}
                      >
                        Rasm almashtirish
                      </Button>
                    </Upload>
                    {photo && (
                      <Button
                        danger
                        icon={<Trash2 size={14} style={{ marginRight: 6 }} />}
                        onClick={handleRemovePhoto}
                        style={{ borderRadius: 6 }}
                      >
                        O'chirish
                      </Button>
                    )}
                  </Space>
                </div>
              </Card>

              {/* Card 2: Shaxsiy Ma'lumotlar */}
              <Card
                size="small"
                title={<span style={{ color: titleColor, fontWeight: 600, fontSize: 14 }}>{t("upEditPersonal")}</span>}
                style={{
                  borderRadius: 8,
                  backgroundColor: cardBg,
                  borderColor: cardBorder,
                }}
              >
                <Row gutter={[16, 14]}>
                  <Col span={12}>
                    <Typography.Text style={{ color: labelColor, fontSize: 13, fontWeight: 500 }}>
                      To'liq Ism (F.I.O):
                    </Typography.Text>
                    <Input
                      prefix={<User size={14} color="#94A3B8" />}
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      style={{ marginTop: 6, ...inputStyle }}
                    />
                  </Col>

                  <Col span={12}>
                    <Typography.Text style={{ color: labelColor, fontSize: 13, fontWeight: 500 }}>
                      Telefon raqami:
                    </Typography.Text>
                    <Input
                      prefix={<Phone size={14} color="#94A3B8" />}
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      style={{ marginTop: 6, ...inputStyle }}
                    />
                  </Col>

                  {/* Email with Verification Flow */}
                  <Col span={12}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography.Text style={{ color: labelColor, fontSize: 13, fontWeight: 500 }}>
                        Elektron pochta (Email):
                      </Typography.Text>
                      {emailInput === savedEmail && isEmailVerified && (
                        <Tag color="success" style={{ fontSize: 11, borderRadius: 4, marginRight: 0 }}>
                          ✓ Tasdiqlangan
                        </Tag>
                      )}
                    </div>

                    <Input
                      prefix={<Mail size={14} color="#94A3B8" />}
                      value={emailInput}
                      onChange={(e) => {
                        setEmailInput(e.target.value)
                        if (e.target.value !== savedEmail) {
                          setIsEmailVerified(false)
                        }
                      }}
                      style={{ marginTop: 6, ...inputStyle }}
                    />

                    {emailInput !== savedEmail && (
                      <div style={{ marginTop: 6 }}>
                        <Button
                          size="small"
                          type="primary"
                          icon={<Send size={12} style={{ marginRight: 4 }} />}
                          onClick={handleSendVerificationCode}
                          disabled={codeCountdown > 0}
                          style={{ borderRadius: 4, fontSize: 12, backgroundColor: "#2563EB" }}
                        >
                          {codeCountdown > 0 ? `Qayta yuborish (${codeCountdown}s)` : "Tasdiqlash kodini olish"}
                        </Button>
                      </div>
                    )}

                    {showCodeInput && (
                      <div style={{ marginTop: 8, padding: 8, background: isDark ? "#121826" : "#EFF6FF", borderRadius: 6, border: "1px dashed #3B82F6" }}>
                        <Typography.Text style={{ color: labelColor, fontSize: 12, display: "block", marginBottom: 4 }}>
                          Pochtaga kelgan 6 xonali kodni kiriting:
                        </Typography.Text>
                        <Space>
                          <Input
                            placeholder={t("phCodeExample")}
                            value={inputCode}
                            onChange={(e) => setInputCode(e.target.value)}
                            maxLength={6}
                            style={{ width: 120, height: 32, ...inputStyle }}
                          />
                          <Button
                            type="primary"
                            size="small"
                            onClick={handleVerifyEmail}
                            style={{ height: 32, backgroundColor: "#10B981", borderColor: "#10B981" }}
                          >
                            Tasdiqlash
                          </Button>
                        </Space>
                      </div>
                    )}
                  </Col>

                  <Col span={12}>
                    <Typography.Text style={{ color: labelColor, fontSize: 13, fontWeight: 500 }}>
                      Tizimdagi lavozim:
                    </Typography.Text>
                    <Input
                      prefix={<Shield size={14} color="#94A3B8" />}
                      value={userRole}
                      disabled
                      style={{ marginTop: 6, ...inputStyle, opacity: 0.8 }}
                    />
                  </Col>
                </Row>

                <Alert
                  type="info"
                  showIcon
                  icon={<Info size={14} />}
                  message={String(i18n.t("emailPurposeNote", { username }))}
                  style={{ marginTop: 14, fontSize: 12, borderRadius: 6 }}
                />

                <div style={{ marginTop: 14, textAlign: "right" }}>
                  <Button
                    type="primary"
                    icon={<Save size={14} style={{ marginRight: 6 }} />}
                    onClick={handleSavePersonalInfo}
                    style={{ borderRadius: 6, backgroundColor: "#2563EB" }}
                  >
                    Ma'lumotlarni Saqlash
                  </Button>
                </div>
              </Card>
            </div>
          </Tabs.TabPane>

          {/* TAB 2: Xavfsizlik va Parol */}
          <Tabs.TabPane
            key="security"
            tab={
              <Space>
                <Key size={15} />
                <span>Xavfsizlik & Parol</span>
              </Space>
            }
          >
            <div style={{ marginTop: 8 }}>
              <Card
                size="small"
                title={<span style={{ color: titleColor, fontWeight: 600, fontSize: 14 }}>Parolni Yangilash</span>}
                style={{
                  borderRadius: 8,
                  backgroundColor: cardBg,
                  borderColor: cardBorder,
                }}
              >
                <Form layout="vertical" form={pwdForm} onFinish={handlePasswordSubmit}>
                  <Form.Item
                    label={<span style={{ color: labelColor, fontWeight: 500 }}>{t("upCurrentPassword")}</span>}
                    name="currentPassword"
                    rules={[{ required: true, message: "Joriy parolingizni kiriting!" }]}
                  >
                    <Input.Password
                      prefix={<Key size={14} color="#94A3B8" />}
                      placeholder={t("phEnterCurrentPassword")}
                      style={inputStyle}
                    />
                  </Form.Item>

                  <Form.Item
                    label={<span style={{ color: labelColor, fontWeight: 500 }}>{t("upNewPassword")}</span>}
                    name="newPassword"
                    rules={[{ required: true, message: String(i18n.t("enterNewPassword")) }, { min: 6, message: String(i18n.t("pwdMinChars")) }]}
                  >
                    <Input.Password
                      prefix={<Key size={14} color="#94A3B8" />}
                      placeholder={t("phNewPassword")}
                      onChange={(e) => handlePasswordStrength(e.target.value)}
                      style={inputStyle}
                    />
                  </Form.Item>

                  {pwdStrength > 0 && (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 2 }}>
                        <span style={{ color: labelColor }}>{t("upPasswordStrength")}</span>
                        <span style={{ color: pwdStrength < 50 ? "#EF4444" : pwdStrength < 80 ? "#F59E0B" : "#10B981", fontWeight: 600 }}>
                          {pwdStrength < 50 ? i18n.t("pwdWeak") : pwdStrength < 80 ? i18n.t("pwdMedium") : i18n.t("pwdStrong")}
                        </span>
                      </div>
                      <Progress
                        percent={pwdStrength}
                        showInfo={false}
                        status={pwdStrength < 50 ? "exception" : pwdStrength < 80 ? "active" : "success"}
                        strokeColor={pwdStrength < 50 ? "#EF4444" : pwdStrength < 80 ? "#F59E0B" : "#10B981"}
                      />
                    </div>
                  )}

                  <Form.Item
                    label={<span style={{ color: labelColor, fontWeight: 500 }}>{t("upConfirmNewPassword")}</span>}
                    name="confirmPassword"
                    rules={[{ required: true, message: "Parolni tasdiqlang!" }]}
                  >
                    <Input.Password
                      prefix={<CheckCircle size={14} color="#94A3B8" />}
                      placeholder={t("phRepeatNewPassword")}
                      style={inputStyle}
                    />
                  </Form.Item>

                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={changingPwd}
                    style={{ borderRadius: 6, backgroundColor: "#2563EB" }}
                  >
                    Parolni Saqlash
                  </Button>
                </Form>
              </Card>
            </div>
          </Tabs.TabPane>

          {/* TAB 3: Tizim & Bildirishnoma Sozlamalari */}
          <Tabs.TabPane
            key="preferences"
            tab={
              <Space>
                <Sliders size={15} />
                <span>Sozlamalar</span>
              </Space>
            }
          >
            <div style={{ marginTop: 8 }}>
              <Card
                size="small"
                title={<span style={{ color: titleColor, fontWeight: 600, fontSize: 14 }}>Interfeys va Bildirishnomalar</span>}
                style={{
                  borderRadius: 8,
                  backgroundColor: cardBg,
                  borderColor: cardBorder,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0" }}>
                  <div>
                    <Typography.Text style={{ color: titleColor, fontWeight: 600, display: "block" }}>
                      Tungi Rejim (Dark Theme)
                    </Typography.Text>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      Tizimni qorong'u rejimga o'tkazish
                    </Typography.Text>
                  </div>
                  <Switch
                    checked={isDark}
                    onChange={(checked) => {
                      themeDispatch({
                        type: ThemeActions.ChangeTheme,
                        payload: {
                          currentTheme: checked ? "dark" : "default",
                        },
                      })
                    }}
                    checkedChildren={<Moon size={12} />}
                    unCheckedChildren={<Sun size={12} />}
                  />
                </div>

                <Divider style={{ margin: "6px 0", borderColor: cardBorder }} />

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0" }}>
                  <div>
                    <Typography.Text style={{ color: titleColor, fontWeight: 600, display: "block" }}>
                      Ovozli Bildirishnomalar
                    </Typography.Text>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      Yangi buyurtma va xabar kelganda tovush chiqarish
                    </Typography.Text>
                  </div>
                  <Switch
                    checked={soundEnabled}
                    onChange={(checked) => {
                      setSoundEnabled(checked)
                      localStorage.setItem("crm_sound_enabled", String(checked))
                      message.success(t("msgSoundUpdated"))
                    }}
                  />
                </div>

                {/*
                  "Tizim tili" bo'limi olib tashlandi: til sarlavhadagi
                  tanlagichdan boshqariladi. Bu yerdagi tugmalar tanlovni
                  saqlamasdi va sahifani qayta yuklamasdi, shuning uchun
                  til yarim almashib qolardi.
                */}
              </Card>
            </div>
          </Tabs.TabPane>
        </Tabs>
      </div>
    </Modal>
  )
}
